import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Check, X, Loader2, RefreshCw, ArrowLeft, CreditCard, Users, BarChart3, Eye, UserCheck, TrendingUp } from "lucide-react";
import AdminCustomers from "@/components/AdminCustomers";
import AdminFinance from "@/components/AdminFinance";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminRightsIssue from "@/components/AdminRightsIssue";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const STATUS = [
  { id: "waiting_verification", label: "Menunggu Verifikasi" },
  { id: "pending", label: "Belum Konfirmasi" },
  { id: "approved", label: "Disetujui" },
  { id: "rejected", label: "Ditolak" },
  { id: "", label: "Semua" },
];
const badge = (s) => s === "approved" ? "bg-[var(--bull)]/15 text-bull" : s === "rejected" ? "bg-[var(--bear)]/15 text-bear" : s === "waiting_verification" ? "bg-[var(--amber)]/15 text-amber" : "bg-[var(--surface-hover)] text-[var(--text-secondary)]";

export default function AdminOrders() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("waiting_verification");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState("payments");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    api.get("/admin/check").then((r) => {
      setAllowed(r.data.is_admin);
      if (!r.data.is_admin) toast.error("Halaman khusus admin.");
    }).catch(() => setAllowed(false));
  }, [authLoading, user]);

  useEffect(() => {
    if (allowed) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [allowed]);

  const load = () => {
    setLoading(true);
    api.get(`/admin/orders${filter ? `?status=${filter}` : ""}`)
      .then((r) => { setOrders(r.data.orders || []); setCounts(r.data.counts || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (allowed) load(); /* eslint-disable-next-line */ }, [allowed, filter]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/orders/${id}/${action}`, action === "reject" ? { reason: "" } : {});
      toast.success(action === "approve" ? "Pembayaran disetujui, membership aktif." : "Pesanan ditolak.");
      load();
    } catch (e) {
      toast.error("Gagal memproses.");
    } finally { setBusy(null); }
  };

  if (allowed === null) return <div className="min-h-screen flex items-center justify-center bg-[var(--surface-base)]"><Loader2 className="animate-spin text-accent" /></div>;
  if (!allowed) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[var(--surface-base)] text-center px-6">
      <ShieldCheck size={40} className="text-[var(--text-muted)]" />
      <p className="text-[var(--text-secondary)]">Halaman ini khusus admin.</p>
      <button onClick={() => navigate("/")} className="h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold">Kembali</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--surface-base)]">
      <div className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/")} className="p-2 rounded-lg hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]" data-testid="admin-back"><ArrowLeft size={18} /></button>
          <ShieldCheck size={22} className="text-accent" />
          <h1 className="font-heading font-extrabold text-2xl">Panel Admin</h1>
          {tab === "payments" && <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-accent"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
          <div className="card p-4" data-testid="stat-visits">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-1"><Eye size={14} className="text-accent" /> Kunjungan Website</div>
            <p className="font-mono-data font-extrabold text-xl">{stats ? Number(stats.visits.total).toLocaleString("id-ID") : "…"}</p>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{stats ? `${stats.visits.unique.toLocaleString("id-ID")} pengunjung unik` : ""}</p>
          </div>
          <div className="card p-4" data-testid="stat-visits-today">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-1"><BarChart3 size={14} className="text-accent" /> Kunjungan Hari Ini</div>
            <p className="font-mono-data font-extrabold text-xl">{stats ? Number(stats.visits.today).toLocaleString("id-ID") : "…"}</p>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{stats ? `${stats.visits.month.toLocaleString("id-ID")} bulan ini` : ""}</p>
          </div>
          <div className="card p-4" data-testid="stat-users">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-1"><UserCheck size={14} className="text-accent" /> Pelanggan Login Email</div>
            <p className="font-mono-data font-extrabold text-xl">{stats ? Number(stats.users.total).toLocaleString("id-ID") : "…"}</p>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{stats ? `+${stats.users.new_this_month} bulan ini` : ""}</p>
          </div>
          <div className="card p-4" data-testid="stat-active-members">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-1"><Users size={14} className="text-bull" /> Member Aktif</div>
            <p className="font-mono-data font-extrabold text-xl text-bull">{stats ? Number(stats.users.active_members).toLocaleString("id-ID") : "…"}</p>
          </div>
        </div>

        <AdminAnalytics stats={stats} />

        <div className="flex gap-2 mb-6 border-b border-[var(--border)]">
          <button onClick={() => setTab("payments")} data-testid="admin-tab-payments" className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "payments" ? "border-[var(--accent)] text-accent" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>
            <CreditCard size={15} /> Pembayaran
          </button>
          <button onClick={() => setTab("customers")} data-testid="admin-tab-customers" className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "customers" ? "border-[var(--accent)] text-accent" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>
            <Users size={15} /> Pelanggan
          </button>
          <button onClick={() => setTab("finance")} data-testid="admin-tab-finance" className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "finance" ? "border-[var(--accent)] text-accent" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>
            <BarChart3 size={15} /> Laporan Keuangan
          </button>
          <button onClick={() => setTab("rights")} data-testid="admin-tab-rights" className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "rights" ? "border-[var(--accent)] text-accent" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>
            <TrendingUp size={15} /> Rights Issue
          </button>
        </div>

        {tab === "customers" ? <AdminCustomers /> : tab === "finance" ? <AdminFinance /> : tab === "rights" ? <AdminRightsIssue /> : (
        <>
        <div className="flex flex-wrap gap-2 mb-5">
          {STATUS.map((s) => (
            <button key={s.id} onClick={() => setFilter(s.id)} data-testid={`admin-filter-${s.id || "all"}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === s.id ? "bg-[var(--accent)] text-[#07090E]" : "bg-[var(--surface-card)] border border-[var(--border)] text-[var(--text-secondary)]"}`}>
              {s.label}{counts[s.id] != null ? ` (${counts[s.id]})` : ""}
            </button>
          ))}
        </div>

        {orders.length === 0 ? (
          <div className="card p-12 text-center text-[var(--text-muted)]">Tidak ada pesanan pada filter ini.</div>
        ) : (
          <div className="space-y-3" data-testid="admin-orders">
            {orders.map((o) => (
              <div key={o.order_id} className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-order-${o.order_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-data text-sm text-accent">{o.order_id}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${badge(o.status)}`}>{o.status}</span>
                    {o.discount_applied && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-accent font-bold">-20%</span>}
                  </div>
                  <p className="text-sm mt-1"><b>{o.package_name}</b> · {o.days} hari · <span className="text-accent font-mono-data">{rupiah(o.amount_total)}</span></p>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">{o.email} · pengirim: <b className="text-[var(--text-secondary)]">{o.sender_name || "-"}</b>{o.note ? ` · "${o.note}"` : ""}</p>
                </div>
                {o.status !== "approved" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => act(o.order_id, "approve")} disabled={busy === o.order_id} data-testid={`approve-${o.order_id}`} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--bull)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
                      {busy === o.order_id ? <Loader2 size={14} className="animate-spin" /> : <Check size={15} />} Setujui
                    </button>
                    <button onClick={() => act(o.order_id, "reject")} disabled={busy === o.order_id} data-testid={`reject-${o.order_id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--surface-hover)] text-bear text-sm font-semibold hover:bg-[var(--bear)]/15"><X size={15} /> Tolak</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
